// src/api/api.js
const BASE_URL = (process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "");
if (!BASE_URL) console.warn("REACT_APP_BASE_URL is empty. Set it in .env and restart dev server.");

function buildUrl(endpoint = "") {
  const e = endpoint.startsWith("/") ? endpoint : `/${endpoint}`;
  return `${BASE_URL}${e}`;
}

function withQuery(url, params) {
  if (!params) return url;
  const u = new URL(url);
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null) u.searchParams.set(k, String(v));
  });
  return u.toString();
}

async function request(method, endpoint, { params, body, headers, timeout = 15000 } = {}) {
  const url = withQuery(buildUrl(endpoint), params);
  const controller = new AbortController();
  const t = setTimeout(() => controller.abort(), timeout);

  try {
    const res = await fetch(url, {
      method,
      headers: { Accept: "application/json", "Content-Type": "application/json", ...headers },
      body: body ? JSON.stringify(body) : undefined,
      signal: controller.signal,
      credentials: "omit", // change to "include" if you use cookie auth
    });
    if (!res.ok) {
      const text = await res.text().catch(() => "");
      throw new Error(`HTTP ${res.status} for ${url}${text ? ` — ${text}` : ""}`);
    }
    const ct = res.headers.get("content-type") || "";
    return ct.includes("application/json") ? res.json() : res.text();
  } finally {
    clearTimeout(t);
  }
}

export function fetchData(endpoint, opts) {
  return request("GET", endpoint, opts);
}

export function postJson(endpoint, body, opts) {
  return request("POST", endpoint, { ...opts, body });
}

// Alias so you can import as postJSON in Contact.jsx
export const postJSON = postJson;

export async function postFormData(endpoint, formData, { params, headers, timeout = 20000 } = {}) {
  const url = withQuery(buildUrl(endpoint), params);
  const controller = new AbortController();
  const t = setTimeout(() => controller.abort(), timeout);

  try {
    const res = await fetch(url, {
      method: "POST",
      headers: { Accept: "application/json", ...headers },
      body: formData,
      signal: controller.signal,
      credentials: "omit", // change to "include" if you use cookie auth
    });
    if (!res.ok) {
      const text = await res.text().catch(() => "");
      throw new Error(`HTTP ${res.status} for ${url}${text ? ` — ${text}` : ""}`);
    }
    const ct = res.headers.get("content-type") || "";
    return ct.includes("application/json") ? res.json() : res.text();
  } finally {
    clearTimeout(t);
  }
}

// If your API returns relative media paths like /media/...
export function toAbsoluteMedia(path) {
  if (!path) return "";
  try {
    return new URL(path).href; // already absolute
  } catch {
    return buildUrl(path.startsWith("/") ? path : `/${path}`);
  }
}
